/**
 * Unified Communication Modal
 * Tabs: WhatsApp, Email, PDF, History
 * Right Sidebar: Tenant Insights
 */

import { useState, useEffect } from 'react';
import { Send, Save, X, MessageSquare, Mail, FileText, Clock, TrendingUp, AlertCircle, Scale } from 'lucide-react';
import communicationService from '@/services/communicationService';
import templateService from '@/services/templateService';
import tenantInsightsService from '@/services/tenantInsightsService';
import rentPaymentsService from '@/services/rentPaymentsService';

export default function UnifiedCommunicationModal({ 
  channel, // default tab
  paymentData, 
  tenantData, 
  propertyData, 
  ownerData,
  onClose,
  onSent,
  arrearsSnapshot = null,
  testMode = false,
  onTestEmailReview,
  onTestEmailSend,
  onTestWhatsAppLocalValidate,
  onTestDryRun,
  onProductionEmailReview,
  onProductionEmailSend,
  onProductionContactSave,
  communicationPurpose = 'arrears'
}) {
  const isBatchArrears = Boolean(arrearsSnapshot?.months?.length);
  const isPaymentReconciliation = communicationPurpose === 'payment_reconciliation';
  const arrearsRows = arrearsSnapshot?.months || [];
  const arrearsTotal = Number(arrearsSnapshot?.total_open_eur || 0);
  const getBatchSignature = (channel) => channel === 'email'
    ? `Mit freundlichen Grüßen\n\nLukas Dreilich\nAssistenz der Geschäftsführung\nVerwaltung\n\nGRN Grundbesitz Rhein-Neckar UG (haftungsbeschränkt)\nKrügerstraße 5 · 68219 Mannheim\nwww.g-rn.de · Telefon: 0621/7709550 · E-Mail: info@g-rn.de\nAmtsgericht Mannheim – Registergericht – HRB 719718`
    : `Freundliche Grüße\nLukas Dreilich · Verwaltung\nGRN Grundbesitz Rhein-Neckar UG (haftungsbeschränkt)\nTelefon: 0621/7709550 · info@g-rn.de`;
  const correspondenceRecipient = () => {
    const fullName = String(tenantData?.name || '{{MIETERNAME_EINTRAGEN}}').replace(/^TESTMIETER\s*[·-]\s*/i, '').trim();
    const storedSalutation = String(tenantData?.salutation || tenantData?.anrede || '').trim();
    const isTestTenant = testMode && /Lukas\s+Dreilich/i.test(fullName);
    if (isTestTenant) return { mode: 'Herr', name: fullName, source: 'test_case_confirmed', evidence: 'Vereinbarter Testfall' };
    if (storedSalutation === 'Herr' || storedSalutation === 'Frau') return { mode: storedSalutation, name: String(tenantData?.contactPerson || tenantData?.contact_person || fullName).trim(), source: 'confirmed_master_data', evidence: 'Bestätigtes Stammdatum' };
    const candidate = (contractScanState?.data?.correspondence_candidates || []).find((item) => ['exact_tenant_name_in_contract_text', 'full_name_and_titled_surname_in_same_contract_text'].includes(item?.binding) && (item?.salutation === 'Herr' || item?.salutation === 'Frau'));
    if (candidate?.human_verified === true) return { mode: candidate.salutation, name: candidate.addressee_name || fullName, source: 'verified_contract_evidence', evidence: `${candidate.filename || 'Mietvertragsdokument'} · ${candidate.quote || 'human-verifiziert'}` };
    if (candidate) return { mode: 'neutral', name: String(tenantData?.contactPerson || tenantData?.contact_person || fullName).trim(), source: 'contract_candidate_review_required', evidence: `Anredekandidat ${candidate.quote || ''} aus ${candidate.filename || 'Mietvertragsdokument'} · Bestätigung erforderlich` };
    return { mode: 'neutral', name: String(tenantData?.contactPerson || tenantData?.contact_person || fullName).trim(), source: 'neutral_fallback', evidence: 'Keine bestätigte oder exakt belegte persönliche Anrede' };
  };
  const batchSalutation = () => {
    const recipient = correspondenceRecipient();
    const lastName = String(recipient.name || '').split(/\s+/).filter(Boolean).pop() || recipient.name;
    if (recipient.mode === 'Herr') return `Sehr geehrter Herr ${lastName},`;
    if (recipient.mode === 'Frau') return `Sehr geehrte Frau ${lastName},`;
    return `Guten Tag ${recipient.name},`;
  };
  const contractRuleTextForDraft = (stage = 0) => {
    if (Number(stage) === 0) return '';
    const verifiedDue = (contractRuleState?.data?.facts?.due_date || []).find((fact) => fact.human_verified);
    if (verifiedDue) return `Vertragsregel laut human-verifiziertem Vertragsfakt:\n${verifiedDue.value || verifiedDue.evidence?.quote || '{{FÄLLIGKEITSREGEL_PRÜFEN}}'}`;
    const scan = contractScanState?.data;
    const dueCandidate = scan?.rule_candidates?.due_date?.[0];
    const defaultCandidate = scan?.rule_candidates?.default_or_dunning?.[0];
    if (dueCandidate || defaultCandidate) {
      const candidate = dueCandidate || defaultCandidate;
      return `Vertragsregel-Kandidat aus ${candidate.filename || 'Mietvertragsdokument'} (Review erforderlich):\n${candidate.quote || '{{VERTRAGSREGEL_PRÜFEN}}'}\nDiese Klausel wird nicht als Gebührenforderung übernommen.`;
    }
    return '';
  };
  const dunningHistoryTextForDraft = (stage = 0) => {
    const requiredPreviousLevel = Number(stage) === 2 ? 1 : Number(stage) === 3 ? 2 : null;
    if (requiredPreviousLevel === null) return '';
    const event = (dunningHistoryState?.data?.history || []).find((entry) => Number(entry?.dunning_level) === requiredPreviousLevel && /^sent_/.test(String(entry?.status || '')));
    if (!event?.sent_at) return '';
    const sentAt = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(event.sent_at));
    const label = requiredPreviousLevel === 1 ? 'Zahlungserinnerung – Stufe 1' : 'Erste Mahnung – Stufe 2';
    return `Vorangegangene Kommunikation: ${label} wurde am ${sentAt} Uhr per E-Mail versandt.`;
  };
  const batchText = (channel, stage = 0) => {
    const normalizedStage = [0, 1, 2, 3].includes(Number(stage)) ? Number(stage) : 0;
    const paymentRows = arrearsRows.map((row) => `${row.month_label}: Soll ${Number(row.expected_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}, bereits bezahlt ${Number(row.received_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}, offen ${Number(row.outstanding_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`).join('\n');
    if (isPaymentReconciliation) {
      const reconciliationRows = arrearsRows.map((row) => `${row.month_label}: Soll ${Number(row.expected_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}, im System erfasster Bankeingang ${Number(row.received_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`).join('\n');
      return [
        'ZAHLUNGSABGLEICH',
        batchSalutation(),
        'wir prüfen derzeit die Zuordnung einer Zahlung zu Ihrer Mietforderung. Dies ist keine Zahlungsaufforderung und keine Mahnung.',
        `Prüfstand\n${reconciliationRows || '{{ZAHLUNGSABGLEICH_EINTRAGEN}}'}`,
        arrearsSnapshot?.control_note || 'Sollbasis und Zielperiode werden intern geprüft.',
        'Bitte teilen Sie uns mit, falls Sie zu diesem Zahlungseingang ergänzende Angaben oder Unterlagen haben.',
        getBatchSignature(channel),
      ].filter(Boolean).join('\n\n');
    }
    const total = arrearsTotal.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    const stageCopy = {
      0: {
        heading: 'ZAHLUNGSÜBERSICHT',
        body: 'nach unserem aktuellen Stand ergeben sich für die folgenden Monate noch offene Beträge. Bitte prüfen Sie die Übersicht und melden Sie sich, falls eine Zahlung noch nicht berücksichtigt wurde.',
        paymentDetails: '',
        history: '',
      },
      1: {
        heading: 'ZAHLUNGSERINNERUNG – STUFE 1',
        body: 'wir möchten Sie freundlich daran erinnern, dass die folgenden Beträge nach unserem aktuellen Stand noch offen sind. Bitte prüfen Sie die Übersicht und überweisen Sie den offenen Betrag, sofern keine Zahlung mehr aussteht.',
        paymentDetails: 'Bankverbindung: {{BANKVERBINDUNG_EINTRAGEN}}\nVerwendungszweck: {{VERWENDUNGSZWECK_EINTRAGEN}}',
        history: '',
      },
      2: {
        heading: 'ERSTE MAHNUNG – STUFE 2',
        body: 'zu den folgenden Monatsbeträgen besteht nach unserem aktuellen Stand weiterhin ein offener Betrag. Bitte prüfen Sie die Übersicht und begleichen Sie den offenen Betrag innerhalb von 7 Tagen, sofern keine Zahlung mehr aussteht.',
        paymentDetails: 'Bankverbindung: {{BANKVERBINDUNG_EINTRAGEN}}\nVerwendungszweck: {{VERWENDUNGSZWECK_EINTRAGEN}}',
        history: 'Vorangegangene Kommunikation / Mahnhistorie: {{MAHNHISTORIE_PRÜFEN}}',
      },
      3: {
        heading: 'LETZTE MAHNUNG – STUFE 3',
        body: 'zu den folgenden Monatsbeträgen besteht nach unserem aktuellen Stand weiterhin ein offener Betrag. Bitte prüfen Sie die Übersicht und begleichen Sie den offenen Betrag innerhalb von 3 Tagen, sofern keine Zahlung mehr aussteht.',
        paymentDetails: 'Bankverbindung: {{BANKVERBINDUNG_EINTRAGEN}}\nVerwendungszweck: {{VERWENDUNGSZWECK_EINTRAGEN}}',
        history: 'Vorangegangene Kommunikation / Mahnhistorie: {{MAHNHISTORIE_PRÜFEN}}',
      },
    }[normalizedStage];
    return [
      stageCopy.heading,
      batchSalutation(),
      stageCopy.body,
      contractRuleTextForDraft(normalizedStage),
      `Zahlungsübersicht\n${paymentRows || '{{ZAHLUNGSÜBERSICHT_EINTRAGEN}}'}`,
      `Gesamt offen: ${total}`,
      stageCopy.paymentDetails,
      dunningHistoryTextForDraft(normalizedStage),
      getBatchSignature(channel),
    ].filter(Boolean).join('\n\n');
  };
  const batchSubject = (stage = 0) => {
    if (isPaymentReconciliation) return `Zahlungsabgleich – ${tenantData?.name || '{{MIETERNAME_EINTRAGEN}}'}`;
    return ({
    0: `Zahlungsübersicht – ${tenantData?.name || '{{MIETERNAME_EINTRAGEN}}'}`,
    1: `Zahlungserinnerung – Stufe 1 – ${tenantData?.name || '{{MIETERNAME_EINTRAGEN}}'}`,
    2: 'Erste Mahnung – Stufe 2 – offene Mietzahlungen',
    3: 'Letzte Mahnung – Stufe 3 – offene Mietzahlungen',
  }[Number(stage)] || `Zahlungsübersicht – ${tenantData?.name || '{{MIETERNAME_EINTRAGEN}}'}`);
  };

  // Tab system
  const [activeTab, setActiveTab] = useState(channel || (isPaymentReconciliation ? 'email' : 'whatsapp'));
  
  // Insights
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(true);
  
  // Templates & content
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dunningLevel, setDunningLevel] = useState(isPaymentReconciliation ? 0 : 1);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  
  // Contact info
  const [phone, setPhone] = useState(tenantData.phone || '');
  const [email, setEmail] = useState(tenantData.email || '');
  const [saveToTenant, setSaveToTenant] = useState(false);
  const [recipientConfirmed, setRecipientConfirmed] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [includePdf, setIncludePdf] = useState(true);
  const [testWhatsAppPhoneDraft, setTestWhatsAppPhoneDraft] = useState('');
  const [confirmedTestWhatsAppPhone, setConfirmedTestWhatsAppPhone] = useState('');
  const testWhatsAppPhoneIsConfirmed = Boolean(testWhatsAppPhoneDraft.trim() && confirmedTestWhatsAppPhone === testWhatsAppPhoneDraft.trim());
  const [testEmailReview, setTestEmailReview] = useState({ status: 'idle', remaining: 5, readyAt: 0, token: null, attachment: null });
  const [contractRuleState, setContractRuleState] = useState({ status: 'idle', data: null, error: null });
  const [contractScanState, setContractScanState] = useState({ status: 'idle', data: null, error: null });
  const [dunningHistoryState, setDunningHistoryState] = useState({ status: 'idle', data: null, error: null });
  const testEmailReviewReady = testEmailReview.status === 'ready' && Boolean(testEmailReview.token);
  const renderContractScanStatus = () => {
    const scan = contractScanState.data;
    if (contractScanState.status === 'loading') return <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-950 dark:border-sky-800 dark:bg-sky-950/25 dark:text-sky-100"><div className="font-semibold">Mietvertrags-Scan läuft</div><p className="mt-1 text-xs">Clara360 prüft bekannte Vertrags- und Nachtragsquellen read-only. Es werden keine Vertragsfakten, Gebühren oder Mahnungen angelegt.</p></div>;
    if (contractScanState.status === 'error') return <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-100"><div className="font-semibold">Mietvertrags-Scan nicht verfügbar</div><p className="mt-1 text-xs">Der Entwurf bleibt ohne Mahnkosten. Bitte starten Sie die read-only Prüfung erneut, bevor Sie eine Mahnung überprüfen.</p><button type="button" onClick={() => void runContractScan(dunningLevel)} className="mt-2 rounded-md border border-amber-500 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 dark:bg-slate-950 dark:text-amber-100">Scan erneut starten</button></div>;
    if (contractScanState.status === 'ready') return <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-950 dark:border-sky-800 dark:bg-sky-950/25 dark:text-sky-100"><div className="flex items-center justify-between gap-3"><div className="font-semibold">Mietvertrags-Scan · read-only abgeschlossen</div><button type="button" onClick={() => void runContractScan(dunningLevel)} className="shrink-0 rounded-md border border-sky-500 bg-white px-3 py-1.5 text-xs font-semibold text-sky-900 hover:bg-sky-100 dark:bg-slate-950 dark:text-sky-100">Erneut scannen</button></div><p className="mt-1 text-xs">{scan?.summary?.scanned_documents || 0} Dokument(e) gelesen · {scan?.summary?.rule_sources || 0} Vertragsregel-Kandidat(en) · {scan?.summary?.source_unavailable || 0} Quelle(n) nicht lesbar.</p><p className="mt-1 text-xs">Automatischer Betrag: 0,00 € · Scanresultate sind Kandidaten und ändern keine Vertrags- oder Finanzdaten.</p></div>;
    return <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"><div className="font-semibold">Mietvertrags-Scan</div><p className="mt-1 text-xs">Beim Auswählen einer Mahnstufe startet Clara360 den read-only Scan automatisch. Sie können ihn auch manuell starten.</p><button type="button" onClick={() => void runContractScan(dunningLevel)} className="mt-2 rounded-md border border-slate-500 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-100">Mietvertrag jetzt scannen</button></div>;
  };
  const renderFeeRuleStatus = () => {
    const rule = contractRuleState.data?.fee_rule;
    const contractGate = contractRuleState.data?.gates?.contract;
    if (contractRuleState.status === 'loading') return <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"><div className="font-semibold">Mahnkostenregel · vertragsgebunden</div><p className="mt-1 text-xs">Vertrags- und Klauselnachweise werden read-only geprüft. Automatischer Betrag: 0,00 €.</p></div>;
    if (contractRuleState.status === 'error') return <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-100"><div className="font-semibold">Mahnkostenregel · Prüfung nicht verfügbar</div><div className="mt-1 font-medium">Automatischer Betrag: 0,00 €</div><p className="mt-1 text-xs">Der Versandentwurf bleibt ohne Zuschlag. Vertrags- und Klauselprüfung muss später erneut read-only geladen werden.</p></div>;
    const feeStatus = rule?.status || 'NO_CLAUSE_EVIDENCE';
    const title = feeStatus === 'CLAUSE_VERIFIED_REVIEW_REQUIRED' ? 'Mahnkostenregel · Klauselbeleg vorhanden' : feeStatus === 'CLAUSE_CANDIDATE_NOT_VERIFIED' ? 'Mahnkostenregel · Klauselkandidat prüfen' : 'Mahnkostenregel · keine belegte Klausel';
    const detail = rule?.detail || 'Kein Zuschlag wird aus einer Mahnstufe abgeleitet.';
    return <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-100"><div className="font-semibold">{title}</div><div className="mt-1 font-medium">Automatischer Betrag: 0,00 €</div><p className="mt-1 text-xs">{detail}</p><p className="mt-1 text-xs">Vertragsstatus: {contractGate?.detail || 'Kein Vertragsstatus geladen.'} Die Regel verändert weder Rückstand noch Versandentwurf.</p></div>;
  };
  const renderDunningHistoryStatus = () => {
    if (!isBatchArrears) return null;
    if (dunningHistoryState.status === 'loading') return <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"><div className="font-semibold">Mahnhistorie wird geladen</div><p className="mt-1 text-xs">Clara360 liest ausschließlich erfolgreich auditierte Zustellungen.</p></div>;
    if (dunningHistoryState.status === 'error') return <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-100"><div className="font-semibold">Mahnhistorie nicht verfügbar</div><p className="mt-1 text-xs">Der Entwurf enthält keine automatisch übernommene Vorkommunikation.</p></div>;
    const events = dunningHistoryState.data?.history || [];
    if (!events.length) return <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"><div className="font-semibold">Mahnhistorie</div><p className="mt-1 text-xs">Für dieses Mietverhältnis ist noch keine erfolgreiche E-Mail-Zustellung dokumentiert.</p></div>;
    const stageName = (level) => ({ 0: 'Zahlungsübersicht', 1: 'Zahlungserinnerung – Stufe 1', 2: 'Erste Mahnung – Stufe 2', 3: 'Letzte Mahnung – Stufe 3' }[Number(level)] || 'Kommunikation ohne Mahnstufenzuordnung');
    return <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/25 dark:text-emerald-100"><div className="font-semibold">Mahnhistorie · erfolgreich zugestellt</div><div className="mt-2 space-y-1 text-xs">{events.slice(0, 3).map((event) => { const sentAt = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(event.sent_at)); return <div key={event.audit_id}>{stageName(event.dunning_level)} · {sentAt} Uhr · per E-Mail</div>; })}</div><p className="mt-2 text-xs">Nur erfolgreich auditierte Zustellungen werden für nachfolgende Mahnstufen übernommen.</p></div>;
  };
  const renderCorrespondenceStatus = () => {
    if (!isBatchArrears) return null;
    const recipient = correspondenceRecipient();
    const title = recipient.mode === 'neutral' ? 'Anrede · neutraler Fallback' : recipient.mode === 'Herr' ? 'Anrede · Herr' : 'Anrede · Frau';
    const tone = recipient.source === 'contract_candidate_review_required' ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-100' : recipient.mode === 'neutral' ? 'border-slate-300 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100' : 'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/25 dark:text-emerald-100';
    return <div className={`rounded-lg border p-3 text-sm ${tone}`}><div className="font-semibold">{title}</div><p className="mt-1 text-xs">{batchSalutation()}</p><p className="mt-1 text-xs">Quelle: {recipient.evidence}. Es werden keine Anreden aus Namen geschätzt und keine Stammdaten verändert.</p></div>;
  };
  const batchStageOptions = [
    { value: 0, label: 'Zahlungsübersicht · neutral' },
    { value: 1, label: 'Zahlungserinnerung' },
    { value: 2, label: 'Mahnung' },
    { value: 3, label: 'Letzte Mahnung' },
  ];
  const runContractScan = async (stage = dunningLevel) => {
    if (!isBatchArrears || !paymentData?.property_id || !paymentData?.unit_id || !tenantData?.name) return;
    const year = Number(String(arrearsRows[0]?.month_key || new Date().toISOString()).slice(0, 4)) || new Date().getFullYear();
    setContractScanState({ status: 'loading', data: null, error: null });
    try {
      const data = await rentPaymentsService.scanContractRule({ propertyId: paymentData.property_id, unitId: paymentData.unit_id, tenantName: tenantData.name, year });
      setContractScanState({ status: 'ready', data, error: null });
      if (activeTab === 'email') { setSubject(batchSubject(stage)); setMessage(batchText('email', stage)); }
      else if (activeTab === 'whatsapp') setMessage(batchText('whatsapp', stage));
    } catch (error) {
      console.warn('Contract document scan unavailable:', error);
      setContractScanState({ status: 'error', data: null, error: error.message || 'unavailable' });
    }
  };
  const applyBatchStage = (value) => {
    const stage = Number(value);
    setDunningLevel(stage);
    setSelectedTemplate(null);
    if (activeTab === 'email') {
      setSubject(batchSubject(stage));
      setMessage(batchText('email', stage));
    } else if (activeTab === 'whatsapp') {
      setMessage(batchText('whatsapp', stage));
    }
    if (stage > 0) void runContractScan(stage);
  };

  // Load data on mount and tab change
  useEffect(() => {
    if (isBatchArrears) { setLoadingInsights(false); return; }
    loadInsights();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      // History is loaded in insights
    } else if (activeTab !== 'pdf') {
      loadTemplateData();
    }
  }, [activeTab, isBatchArrears ? 'batch' : dunningLevel]);

  useEffect(() => {
    if (!isBatchArrears || !paymentData?.property_id || !paymentData?.unit_id || !tenantData?.name) {
      setContractRuleState({ status: 'idle', data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    const year = Number(String(arrearsRows[0]?.month_key || new Date().toISOString()).slice(0, 4)) || new Date().getFullYear();
    setContractRuleState({ status: 'loading', data: null, error: null });
    rentPaymentsService.getContractRule({ propertyId: paymentData.property_id, unitId: paymentData.unit_id, tenantName: tenantData.name, year })
      .then((data) => { if (!cancelled) setContractRuleState({ status: 'ready', data, error: null }); })
      .catch((error) => { if (!cancelled) { console.warn('Contract rule resolver unavailable:', error); setContractRuleState({ status: 'error', data: null, error: error.message || 'unavailable' }); } });
    return () => { cancelled = true; };
  }, [isBatchArrears, paymentData?.property_id, paymentData?.unit_id, tenantData?.name, arrearsRows[0]?.month_key]);

  useEffect(() => {
    if (!isBatchArrears || !paymentData?.property_id || !paymentData?.unit_id || !tenantData?.id) {
      setDunningHistoryState({ status: 'idle', data: null, error: null });
      return undefined;
    }
    let cancelled = false;
    setDunningHistoryState({ status: 'loading', data: null, error: null });
    rentPaymentsService.getDunningHistory({ propertyId: paymentData.property_id, unitId: paymentData.unit_id, tenantId: tenantData.id })
      .then((data) => { if (!cancelled) setDunningHistoryState({ status: 'ready', data, error: null }); })
      .catch((error) => { if (!cancelled) { console.warn('Dunning history unavailable:', error); setDunningHistoryState({ status: 'error', data: null, error: error.message || 'unavailable' }); } });
    return () => { cancelled = true; };
  }, [isBatchArrears, paymentData?.property_id, paymentData?.unit_id, tenantData?.id]);

  useEffect(() => {
    if (!isBatchArrears || Number(dunningLevel) === 0 || dunningHistoryState.status === 'idle') return;
    if (activeTab === 'email') { setSubject(batchSubject(dunningLevel)); setMessage(batchText('email', dunningLevel)); }
    else if (activeTab === 'whatsapp') setMessage(batchText('whatsapp', dunningLevel));
  }, [dunningHistoryState.status, dunningHistoryState.data, isBatchArrears, dunningLevel, activeTab]);

  useEffect(() => {
    if (!isBatchArrears || Number(dunningLevel) === 0 || contractScanState.status === 'idle') return;
    if (activeTab === 'email') { setSubject(batchSubject(dunningLevel)); setMessage(batchText('email', dunningLevel)); }
    else if (activeTab === 'whatsapp') setMessage(batchText('whatsapp', dunningLevel));
  }, [contractScanState.status, contractScanState.data, isBatchArrears, dunningLevel, activeTab]);

  useEffect(() => {
    if (testEmailReview.status !== 'countdown') return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((testEmailReview.readyAt - Date.now()) / 1000));
      setTestEmailReview(current => remaining <= 0
        ? { ...current, status: 'ready', remaining: 0 }
        : { ...current, remaining });
    };
    tick();
    const timer = window.setInterval(tick, 200);
    return () => window.clearInterval(timer);
  }, [testEmailReview.status, testEmailReview.readyAt]);

  async function loadInsights() {
    try {
      setLoadingInsights(true);
      const data = await tenantInsightsService.getTenantInsights(
        tenantData.id,
        paymentData.id
      );
      setInsights(data);
    } catch (error) {
      console.error('Load insights error:', error);
    } finally {
      setLoadingInsights(false);
    }
  }

  async function loadTemplateData() {
    try {
      setLoading(true);
      if (isBatchArrears) {
        const batchVariables = {
          tenant_name: tenantData.name || '{{MIETERNAME_EINTRAGEN}}',
          mieter_name: tenantData.name || '{{MIETERNAME_EINTRAGEN}}',
          property_name: propertyData?.name || propertyData?.property_name || '{{OBJEKT_EINTRAGEN}}',
          objekt_name: propertyData?.name || propertyData?.property_name || '{{OBJEKT_EINTRAGEN}}',
          unit_name: tenantData.unit_name || '{{EINHEIT_EINTRAGEN}}',
          outstanding_list: arrearsRows.map((row) => `${row.month_label}: Soll ${Number(row.expected_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}, bereits bezahlt ${Number(row.received_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}, offen ${Number(row.outstanding_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`).join('\n'),
          total_amount: arrearsTotal.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }),
          offene_betraege: arrearsTotal.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }),
          gesamt_offen: arrearsTotal.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }),
          monate: arrearsRows.map((row) => row.month_label).filter(Boolean).join(', '),
          zahlungsuebersicht: arrearsRows.map((row) => `${row.month_label}: Soll ${Number(row.expected_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}, bereits bezahlt ${Number(row.received_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}, offen ${Number(row.outstanding_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`).join('\n'),
          tenant_salutation: '{{ANREDE_EINTRAGEN}}',
          days: '{{VERZUGSTAGE_PRÜFEN}}',
          owner_name: 'Lukas Dreilich',
          owner_phone: '0621/7709550',
          owner_email: 'info@g-rn.de',
          sender_signature: 'Lukas Dreilich · Assistenz der Geschäftsführung · Verwaltung · GRN Grundbesitz Rhein-Neckar UG (haftungsbeschränkt) · Krügerstraße 5 · 68219 Mannheim · www.g-rn.de · Telefon: 0621/7709550 · E-Mail: info@g-rn.de · Amtsgericht Mannheim – Registergericht – HRB 719718',
        };
        setPreviewData({ dunningLevel: 0, variables: batchVariables });
        setDunningLevel(0);
        if (activeTab === 'whatsapp') setMessage(batchText('whatsapp', 0));
        if (activeTab === 'email') { setSubject(batchSubject(0)); setMessage(batchText('email', 0)); }
        // Altvorlagen aus der Mietkontrolle werden im Forderungsversand nicht als Textquelle geladen.
        // Dadurch können alte Signaturen, Objektadressen oder Reihenfolgen nicht mehr in den Versandentwurf gelangen.
        setTemplates([]);
        setSelectedTemplate(null);
        return;
      }
      
      const preview = await communicationService.getTemplatePreview(
        activeTab,
        paymentData,
        tenantData,
        propertyData,
        ownerData
      );
      
      setPreviewData(preview);
      setDunningLevel(preview.dunningLevel || 1);
      
      // Set initial message from preview
      if (activeTab === 'whatsapp') {
        setMessage(preview.text || '');
      } else if (activeTab === 'email') {
        setSubject(preview.subject || '');
        setMessage(preview.body || '');
      }
      
      // Load custom templates from DB
      const templateList = await templateService.getTemplates(activeTab, preview.dunningLevel || 1);
      setTemplates(templateList);
      
      if (templateList.length > 0) {
        setSelectedTemplate(templateList[0]);
      }
    } catch (error) {
      console.error('Load template data error:', error);
    } finally {
      setLoading(false);
    }
  }

  function selectTemplate(template) {
    const storedStage = Number(template?.dunning_level);
    const stage = [0, 1, 2, 3].includes(storedStage) ? storedStage : Number(dunningLevel);

    if (isBatchArrears) {
      // Gespeicherte Altvorlagen dürfen im Forderungsversand keinen Nachrichtentext mehr liefern.
      // Die Mahnstufe wird ausschließlich durch den kanonischen, versionierten Generator erstellt.
      setSelectedTemplate(null);
      setDunningLevel(stage);
      if (activeTab === 'email') {
        setSubject(batchSubject(stage));
        setMessage(batchText('email', stage));
      } else if (activeTab === 'whatsapp') {
        setMessage(batchText('whatsapp', stage));
      }
      return;
    }

    setSelectedTemplate(template);
    // Bestehender Pfad außerhalb der Zahlungsübersicht.
    if (activeTab === 'email') {
      setSubject(renderTemplateString(template.subject_template || '', previewData?.variables || {}));
      setMessage(renderTemplateString(template.body_template || '', previewData?.variables || {}));
    } else if (activeTab === 'whatsapp') {
      setMessage(renderTemplateString(template.message_template || '', previewData?.variables || {}));
    }
  }
  
  function renderTemplateString(template, variables = {}) {
    if (!template) return '';
    let result = String(template);
    Object.entries(variables).forEach(([key, value]) => {
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const safeValue = value ?? `{{${String(key).toUpperCase()}_EINTRAGEN}}`;
      // Akzeptiert sowohl historische {variable} als auch {{ variable }}-Formate.
      result = result.replace(new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}|\\{\\s*${escapedKey}\\s*\\}`, 'g'), String(safeValue));
    });
    result = result.replace(/\[Bankverbindung\]/gi, '{{BANKVERBINDUNG_EINTRAGEN}}');
    const toManualPlaceholder = (rawToken) => {
      const token = String(rawToken).trim().replace(/[^A-Za-z0-9ÄÖÜäöüß]+/g, '_').replace(/^_+|_+$/g, '').toUpperCase();
      if (!token) return '{{FELD_EINTRAGEN}}';
      if (token.endsWith('_EINTRAGEN') || token.endsWith('_PRÜFEN')) return `{{${token}}}`;
      return `{{${token}_EINTRAGEN}}`;
    };
    result = result.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_, rawToken) => toManualPlaceholder(rawToken));
    return result.replace(/\{(?!\{)\s*([^{}]+?)\s*\}(?!\})/g, (_, rawToken) => toManualPlaceholder(rawToken));
  }

  async function handleStartTestEmailReview() {
    const scanHasNoReadableSource = Number(dunningLevel) > 0 && contractScanState.status === 'ready' && Number(contractScanState.data?.summary?.scanned_documents || 0) === 0 && Number(contractScanState.data?.summary?.source_unavailable || 0) > 0;
    if (isBatchArrears && !isPaymentReconciliation && (contractScanState.status === 'loading' || (Number(dunningLevel) > 0 && contractScanState.status !== 'ready') || scanHasNoReadableSource)) {
      alert('Der Mietvertrags-Scan muss vor der Überprüfung abgeschlossen und mindestens eine Quelle lesbar sein. Bitte starten Sie den Scan erneut oder klären Sie die Dokumentquelle. Mahnkosten bleiben 0,00 € und werden nicht automatisch übernommen.');
      return;
    }
    if (!email.trim() || !subject.trim() || !message.trim()) {
      alert('Bitte prüfen Sie Empfänger, Betreff und Nachricht vor der Überprüfung.');
      return;
    }
    try {
      setTestEmailReview({ status: 'starting', remaining: 5, readyAt: 0, token: null, attachment: null });
      if (!testMode && !recipientConfirmed) throw new Error('Bitte bestätigen Sie den Empfänger vor der Überprüfung.');
      if (!testMode && saveToTenant) await onProductionContactSave?.({ email });
      const review = testMode
        ? await onTestEmailReview?.({ email, subject, message, arrearsSnapshot, includePdf })
        : await onProductionEmailReview?.({ email, subject, message, arrearsSnapshot, includePdf, recipientConfirmed });
      if (!review?.review_token || !review?.not_before_ms) throw new Error('Überprüfungsfreigabe konnte nicht erstellt werden');
      const readyAt = Number(review.not_before_ms);
      setTestEmailReview({ status: 'countdown', remaining: Math.max(1, Math.ceil((readyAt - Date.now()) / 1000)), readyAt, token: review.review_token, attachment: review.attachment || null });
    } catch (error) {
      setTestEmailReview({ status: 'idle', remaining: 5, readyAt: 0, token: null, attachment: null });
      alert('❌ Fehler bei der Überprüfung: ' + error.message);
    }
  }

  function cancelTestEmailReview() {
    setTestEmailReview({ status: 'idle', remaining: 5, readyAt: 0, token: null, attachment: null });
  }

  function downloadReviewedPdf() {
    const attachment = testEmailReview.attachment;
    if (!attachment?.content_base64) return;
    const raw = window.atob(attachment.content_base64);
    const bytes = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
    const url = URL.createObjectURL(new Blob([bytes], { type: attachment.content_type || 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url; link.download = attachment.filename || 'Zahlungsuebersicht.pdf'; link.click();
    URL.revokeObjectURL(url);
  }

  async function handleSend() {
    if (testMode && isBatchArrears && activeTab === 'whatsapp') {
      if (!testWhatsAppPhoneIsConfirmed) return;
      if (!message.trim()) {
        alert('Bitte Nachricht prüfen');
        return;
      }
      try {
        setSending(true);
        const result = await onTestWhatsAppLocalValidate?.({ phone: confirmedTestWhatsAppPhone, message, arrearsSnapshot, includePdf });
        if (!result?.ok || !result?.local_only || result?.meta_called !== false) throw new Error('Lokaler WhatsApp-Test konnte nicht bestätigt werden');
        alert('✅ Lokaler WhatsApp-Test erfolgreich. Nummer, Nachricht und PDF-Option wurden geprüft; Meta wurde nicht aufgerufen und keine WhatsApp versendet.');
      } catch (error) {
        alert('❌ Lokaler WhatsApp-Test fehlgeschlagen: ' + error.message);
      } finally {
        setSending(false);
      }
      return;
    }

    // Validation
    if (activeTab === 'whatsapp' && !phone.trim()) {
      alert('Bitte Telefonnummer eingeben');
      return;
    }
    if (activeTab === 'email' && !email.trim()) {
      alert('Bitte E-Mail-Adresse eingeben');
      return;
    }
    if (!message.trim()) {
      alert('Bitte Nachricht eingeben');
      return;
    }

    try {
      setSending(true);

      if (testMode && isBatchArrears && activeTab === 'email') {
        // Der finale Versand benötigt einen kurzlebigen, servergebundenen Review-Token.
        if (!testEmailReviewReady) return;
        const result = await onTestEmailSend?.({ email, subject, message, arrearsSnapshot, reviewToken: testEmailReview.token, includePdf });
        if (!result?.ok) throw new Error('Test-E-Mail konnte nicht bestätigt werden');
        alert('✅ Test-E-Mail wurde an den bestätigten Testkontakt zugestellt und auditiert.');
        onClose();
        return;
      }

      if (!testMode && isBatchArrears && activeTab === 'email') {
        if (!testEmailReviewReady || !recipientConfirmed) return;
        const result = await onProductionEmailSend?.({ email, subject, message, arrearsSnapshot, reviewToken: testEmailReview.token, includePdf, recipientConfirmed });
        if (!result?.ok) throw new Error('E-Mail konnte nicht bestätigt werden');
        alert(isPaymentReconciliation ? '✅ E-Mail wurde einzeln zugestellt und als Zahlungsabgleich auditiert.' : '✅ E-Mail wurde einzeln zugestellt und mit Zahlungsübersicht auditiert.');
        onClose();
        return;
      }

      if (testMode) {
        await onTestDryRun?.({ channel: activeTab, email, phone, subject, message, arrearsSnapshot });
        alert('🧪 Test-Dry-Run abgeschlossen. Es wurde keine Nachricht zugestellt.');
        onClose();
        return;
      }

      if (isBatchArrears) {
        alert('✅ Forderungsentwurf vorbereitet. Für den Versand wechseln Sie bitte in den E-Mail-Dialog und schließen die Empfängerüberprüfung ab.');
        onClose();
        return;
      }

      // Update tenant data with current contact info
      const currentTenantData = {
        ...tenantData,
        phone: activeTab === 'whatsapp' ? phone : tenantData.phone,
        email: activeTab === 'email' ? email : tenantData.email
      };

      // Send message using correct service signature
      if (activeTab === 'whatsapp') {
        await communicationService.sendWhatsApp(
          paymentData,
          currentTenantData,
          propertyData,
          ownerData,
          message // custom message
        );
      } else if (activeTab === 'email') {
        await communicationService.sendEmail(
          paymentData,
          currentTenantData,
          propertyData,
          ownerData,
          subject,
          message // custom message
        );
      }
      
      // Save contact to tenant if changed and checkbox is checked
      if (saveToTenant) {
        // TODO: Update tenant contact in database
        console.log('Save contact to tenant:', currentTenantData);
      }

      alert('✅ Nachricht gesendet!');
      if (onSent) onSent();
      onClose();
    } catch (error) {
      alert('❌ Fehler: ' + error.message);
    } finally {
      setSending(false);
    }
  }

  async function handleSaveTemplate() {
    if (!newTemplateName.trim()) {
      alert('Bitte Namen eingeben');
      return;
    }

    try {
      await templateService.createTemplate({
        name: newTemplateName,
        channel: activeTab,
        dunning_level: dunningLevel,
        message_template: activeTab === 'whatsapp' ? message : null,
        subject_template: activeTab === 'email' ? subject : null,
        body_template: activeTab === 'email' ? message : null,
        description: `Benutzerdefinierte Vorlage für Stufe ${dunningLevel}`
      });
      
      alert('✅ Vorlage gespeichert!');
      setShowSaveDialog(false);
      setNewTemplateName('');
      loadTemplateData();
    } catch (error) {
      alert('❌ Fehler: ' + error.message);
    }
  }

  // Check if legal tab should be shown (Verzug > 30 days + documented steps)
  const showLegalTab = !isBatchArrears && paymentData.days_overdue > 30;
  
  const tabs = isPaymentReconciliation
    ? [{ id: 'email', label: 'E-Mail', icon: Mail }]
    : [
        { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
        { id: 'email', label: 'E-Mail', icon: Mail },
        { id: 'pdf', label: 'PDF', icon: FileText },
        { id: 'history', label: 'Verlauf', icon: Clock },
        ...(showLegalTab ? [{ id: 'legal', label: '⚖️ Gerichtlich', icon: Scale }] : [])
      ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex">
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Kommunikation
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {tenantData.name} | {isPaymentReconciliation ? 'Zahlungsabgleich · Entwurf' : isBatchArrears ? 'Zahlungsübersicht · Entwurf' : ('Mahnstufe: ' + dunningLevel)}
                </p>
              </div>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {isBatchArrears && <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100">{isPaymentReconciliation ? <><div className="mb-1 font-semibold">Zahlungsabgleich · keine Forderung und keine Mahnung</div><div>{arrearsRows.map(row => `${row.month_label}: Soll ${Number(row.expected_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} · Bankeingang ${Number(row.received_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`).join(' | ')}</div><div className="mt-1">{arrearsSnapshot?.control_note || 'Sollbasis und Zielperiode werden geprüft.'}</div></> : <><div className="mb-2 font-semibold">Ausgewählte Monatsübersicht · Gesamt offen: {arrearsTotal.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div><div className="overflow-x-auto"><table className="w-full min-w-[520px] text-left"><thead><tr className="border-b border-violet-200 dark:border-violet-800"><th className="py-1">Monat</th><th className="py-1 text-right">Soll</th><th className="py-1 text-right">Bezahlt</th><th className="py-1 text-right">Offen</th></tr></thead><tbody>{arrearsRows.map(row => <tr key={row.month_key}><td className="py-1">{row.month_label}</td><td className="py-1 text-right">{Number(row.expected_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td><td className="py-1 text-right">{Number(row.received_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td><td className="py-1 text-right font-semibold">{Number(row.outstanding_eur || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td></tr>)}</tbody></table></div></>}</div>}

            {/* Tabs */}
            <div className="flex gap-2 mt-4">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading && activeTab !== 'history' ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {/* WhatsApp Tab */}
                {activeTab === 'whatsapp' && (
                  <div className="space-y-4">
                    {!(testMode && isBatchArrears) && <>
                    {/* Phone Input */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Telefonnummer</label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+49 176 12345678"
                          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {phone !== tenantData.phone && (
                          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <input
                              type="checkbox"
                              checked={saveToTenant}
                              onChange={(e) => setSaveToTenant(e.target.checked)}
                            />
                            In Stammdaten speichern
                          </label>
                        )}
                      </div>
                      {!phone && (
                        <p className="text-sm text-orange-600 mt-1">⚠️ Keine Nummer hinterlegt</p>
                      )}
                    </div></>}

                    {/* Template Selector */}
                    {!isBatchArrears ? <div>
                      <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Vorlage wählen</label>
                      <select
                        value={selectedTemplate?.id || ''}
                        onChange={(e) => {
                          const template = templates.find(t => t.id === e.target.value);
                          if (template) selectTemplate(template);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="" disabled>{templates.length ? 'Vorlage auswählen …' : 'Keine Vorlage hinterlegt'}</option>
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}{t.dunning_level ? ` · Stufe ${t.dunning_level}` : ''}</option>
                        ))}
                      </select>
                    </div> : <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"><div className="font-semibold">Textquelle</div><p className="mt-1 text-xs">Für Zahlungsübersichten und Mahnstufen wird ausschließlich der kanonische, versionierte Textgenerator verwendet. Historische Vorlagen und ihre alten Signaturen werden nicht in diesen Versandentwurf übernommen.</p></div>}

                    {isBatchArrears && !isPaymentReconciliation && <><div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-950 dark:border-violet-800 dark:bg-violet-950/25 dark:text-violet-100"><label className="mb-2 block font-semibold">Forderungs-/Mahnstufe</label><select value={dunningLevel} onChange={(event) => applyBatchStage(event.target.value)} className="w-full rounded-lg border border-violet-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-violet-500 dark:bg-slate-950 dark:text-white">{batchStageOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select><p className="mt-2 text-xs">Manuelle Auswahl. Soll, bereits bezahlt und offen stammen aus der Zahlungsübersicht. Bei einer Mahnstufe scannt Clara360 bekannte Mietvertragsquellen read-only; unbelegte Angaben bleiben offen.</p></div>{renderContractScanStatus()}{renderCorrespondenceStatus()}{renderFeeRuleStatus()}{renderDunningHistoryStatus()}</>}

                    {testMode && isBatchArrears && <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-100"><div className="font-semibold">{testWhatsAppPhoneIsConfirmed ? 'Testnummer lokal bestätigt' : testWhatsAppPhoneDraft.trim() ? 'Testnummer zur Bestätigung bereit' : 'Keine Telefonnummer gefunden'}</div><p className="mt-1 text-xs">{testWhatsAppPhoneIsConfirmed ? `Lokal bestätigt: ${confirmedTestWhatsAppPhone}. Die Nummer wurde nicht gespeichert und wird nicht angeschrieben.` : testWhatsAppPhoneDraft.trim() ? `Eingegeben: ${testWhatsAppPhoneDraft.trim()}. Bitte mit „Testnummer lokal bestätigen“ übernehmen.` : 'Für den Testmieter ist noch keine gebundene WhatsApp-Testnummer hinterlegt. Die Nummer wird erst nach einer separaten Testkontakt-Bestätigung für eine Zustellung verwendet.'}</p><input type="tel" value={testWhatsAppPhoneDraft} onChange={(event) => setTestWhatsAppPhoneDraft(event.target.value)} onInput={(event) => setTestWhatsAppPhoneDraft(event.currentTarget.value)} inputMode="tel" autoComplete="tel" placeholder="+49 176 12345678" aria-label="WhatsApp-Testnummer" className="mt-3 w-full rounded-lg border border-rose-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:bg-slate-950 dark:text-white" /><button type="button" disabled={!testWhatsAppPhoneDraft.trim() || testWhatsAppPhoneIsConfirmed} onClick={() => setConfirmedTestWhatsAppPhone(testWhatsAppPhoneDraft.trim())} className="mt-2 w-full rounded-lg bg-rose-700 px-3 py-2 text-sm font-medium text-white transition-transform hover:bg-rose-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50">{testWhatsAppPhoneIsConfirmed ? 'Testnummer lokal bestätigt' : 'Testnummer lokal bestätigen'}</button><label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-rose-200 bg-white/70 p-3 text-xs text-rose-950 dark:border-rose-800 dark:bg-black/10 dark:text-rose-100"><input type="checkbox" checked={includePdf} onChange={(event) => setIncludePdf(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-rose-300 text-rose-700 focus:ring-rose-500" /><span><span className="font-semibold">Zahlungsübersicht als PDF für WhatsApp vorbereiten</span><span className="mt-0.5 block">{includePdf ? 'Die spätere WhatsApp-Überprüfung erzeugt diese PDF serverseitig aus exakt den ausgewählten Monaten und bindet sie an den finalen Versand.' : 'Es wird später nur der Nachrichtentext vorbereitet; keine PDF-Anlage.'}</span></span></label><div className="mt-2 text-xs font-medium">{testWhatsAppPhoneIsConfirmed ? 'Nummer lokal bestätigt · Bindung, Speicherung und Versand weiterhin gesperrt.' : testWhatsAppPhoneDraft.trim() ? 'Nummer lokal erfasst · bitte noch bestätigen. Bindung und Versand bleiben gesperrt.' : 'Versand derzeit gesperrt · keine Nummer wird automatisch gespeichert oder angeschrieben.'}</div></div>}

                    {/* Message Editor */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Nachricht</label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={10}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSend}
                        disabled={sending || (testMode && isBatchArrears && !testWhatsAppPhoneIsConfirmed)}
                        title={testMode && isBatchArrears ? (testWhatsAppPhoneIsConfirmed ? 'Prüft ausschließlich den lokalen Canary-Transport; keine WhatsApp-Zustellung.' : 'Bitte Testnummer lokal bestätigen.') : undefined}
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        {sending ? 'Lokaler Test läuft...' : (testMode && isBatchArrears) ? (testWhatsAppPhoneIsConfirmed ? 'Lokalen WhatsApp-Test prüfen' : testWhatsAppPhoneDraft.trim() ? 'Testnummer bestätigen' : 'Testnummer erforderlich') : testMode ? 'Test-Dry-Run ausführen' : isBatchArrears ? 'Entwurf abschließen (kein Versand)' : 'WhatsApp senden'}
                      </button>
                      <button
                        onClick={() => setShowSaveDialog(true)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Als Vorlage speichern
                      </button>
                    </div>
                  </div>
                )}

                {/* Email Tab */}
                {activeTab === 'email' && (
                  <div className="space-y-4">
                    {!(!testMode && isBatchArrears) && <>
                    {/* Email Input */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">E-Mail-Adresse</label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); if (!testMode) setRecipientConfirmed(false); }}
                          disabled={testMode && isBatchArrears}
                          placeholder="mieter@example.com"
                          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {!isBatchArrears && email !== tenantData.email && (
                          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <input
                              type="checkbox"
                              checked={saveToTenant}
                              onChange={(e) => setSaveToTenant(e.target.checked)}
                            />
                            In Stammdaten speichern
                          </label>
                        )}
                      </div>
                      {!email && (
                        <p className="text-sm text-orange-600 mt-1">⚠️ Keine E-Mail hinterlegt</p>
                      )}
                    </div></>}
                      {isBatchArrears && !isPaymentReconciliation && activeTab === 'email' && <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"><input type="checkbox" checked={includePdf} disabled={testEmailReview.status !== 'idle'} onChange={(event) => setIncludePdf(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" /><span><span className="font-semibold">Zahlungsübersicht als PDF beifügen</span><span className="mt-0.5 block text-xs text-slate-600 dark:text-slate-300">Die PDF wird erst beim Start der Überprüfung serverseitig aus genau dieser Monatsübersicht erzeugt. Sie kann danach heruntergeladen werden und wird erst beim finalen Klick beigefügt.</span></span></label>}
                      {isBatchArrears && activeTab === 'email' && <div className={`mt-3 rounded-lg border p-3 text-sm ${testEmailReviewReady ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100' : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100'}`}><div className="flex items-center justify-between gap-3"><div className="font-semibold">{testMode ? 'Testkontakt' : 'Mieteradresse'} · Überprüfung vor Versand</div><span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${testEmailReviewReady ? 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-100' : 'bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100'}`}>{testEmailReviewReady ? 'Überprüfung abgeschlossen' : testEmailReview.status === 'countdown' ? `Noch ${testEmailReview.remaining} Sek.` : testEmailReview.status === 'starting' ? 'Wird vorbereitet' : 'Prüfung erforderlich'}</span></div><p className="mt-1">Empfänger, Betreff, ausgewählte Monate und Nachricht werden unverändert an die Überprüfung gebunden. {includePdf ? 'Die erzeugte PDF-Anlage wird ebenfalls verbindlich geprüft.' : 'Es wird keine PDF-Anlage beigefügt.'} Der Versand erfolgt nie automatisch.</p>{testEmailReview.status === 'idle' && <button type="button" onClick={handleStartTestEmailReview} className="mt-3 rounded-lg bg-amber-700 px-3 py-2 font-medium text-white transition-transform active:scale-[0.97] hover:bg-amber-800">Überprüfung starten</button>}{testEmailReview.status === 'starting' && <div className="mt-3 text-xs font-medium">Überprüfung wird vorbereitet …</div>}{testEmailReview.status === 'countdown' && <div className="mt-3 flex items-center justify-between gap-3"><div className="font-medium">Bitte prüfen Sie die Angaben. Der finale Button wird nach Ablauf aktiviert.</div><button type="button" onClick={cancelTestEmailReview} className="rounded-lg border border-amber-400 bg-white px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100 dark:bg-transparent dark:text-amber-100">Abbrechen</button></div>}{testEmailReview.attachment && <div className="mt-3 flex items-center justify-between gap-3 rounded border border-current/20 bg-white/60 p-2 text-xs dark:bg-black/10"><span className="min-w-0 truncate"><strong>PDF bereit:</strong> {testEmailReview.attachment.filename} · {Math.ceil(Number(testEmailReview.attachment.bytes || 0) / 1024)} KB</span><button type="button" onClick={downloadReviewedPdf} className="shrink-0 rounded border border-current/30 px-2 py-1 font-medium hover:bg-white/70 dark:hover:bg-black/20">PDF herunterladen</button></div>}{testEmailReviewReady && <div className="mt-3 text-xs font-medium">Die Überprüfung ist abgeschlossen. Für die Zustellung klicken Sie jetzt ausdrücklich auf „{testMode ? 'Test-E-Mail jetzt senden' : 'E-Mail jetzt senden'}“.</div>}</div>}

                    {/* Template Selector */}
                    {!isBatchArrears ? <div>
                      <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Vorlage wählen</label>
                      <select
                        value={selectedTemplate?.id || ''}
                        onChange={(e) => {
                          const template = templates.find(t => t.id === e.target.value);
                          if (template) selectTemplate(template);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="" disabled>{templates.length ? 'Vorlage auswählen …' : 'Keine Vorlage hinterlegt'}</option>
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}{t.dunning_level ? ` · Stufe ${t.dunning_level}` : ''}</option>
                        ))}
                      </select>
                    </div> : <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"><div className="font-semibold">Textquelle</div><p className="mt-1 text-xs">Für Zahlungsübersichten und Mahnstufen wird ausschließlich der kanonische, versionierte Textgenerator verwendet. Historische Vorlagen und ihre alten Signaturen werden nicht in diesen Versandentwurf übernommen.</p></div>}

                    {isBatchArrears && !isPaymentReconciliation && <><div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-950 dark:border-violet-800 dark:bg-violet-950/25 dark:text-violet-100"><label className="mb-2 block font-semibold">Forderungs-/Mahnstufe</label><select value={dunningLevel} onChange={(event) => applyBatchStage(event.target.value)} className="w-full rounded-lg border border-violet-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-violet-500 dark:bg-slate-950 dark:text-white">{batchStageOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select><p className="mt-2 text-xs">Manuelle Auswahl. Soll, bereits bezahlt und offen stammen aus der Zahlungsübersicht. Bei einer Mahnstufe scannt Clara360 bekannte Mietvertragsquellen read-only; unbelegte Angaben bleiben offen.</p></div>{renderContractScanStatus()}{renderCorrespondenceStatus()}{renderFeeRuleStatus()}{renderDunningHistoryStatus()}</>}

                    {!testMode && isBatchArrears && <div className={`rounded-lg border p-3 text-sm ${email.trim() ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100' : 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-100'}`}>
                      <div className="font-semibold">{email.trim() ? 'E-Mail-Adresse hinterlegt' : 'Keine E-Mail-Adresse gefunden'}</div>
                      {!email.trim() ? <><p className="mt-1 text-xs">Für diese Zahlungsübersicht ist noch keine E-Mail-Adresse hinterlegt. Bitte geben Sie eine Adresse ein, bevor Sie die Überprüfung starten.</p><input type="email" value={email} onChange={(event) => { setEmail(event.target.value); setRecipientConfirmed(false); }} placeholder="mieter@example.com" className="mt-3 w-full rounded-lg border border-rose-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:bg-slate-950 dark:text-white" /></> : <p className="mt-1 break-all text-xs">{email}</p>}
                      {email.trim() && <label className="mt-3 flex items-start gap-2 text-xs"><input type="checkbox" checked={recipientConfirmed} disabled={testEmailReview.status !== 'idle'} onChange={(event) => setRecipientConfirmed(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" /><span>Ich bestätige, dass diese E-Mail-Adresse für diesen einzelnen ${isPaymentReconciliation ? 'Zahlungsabgleich' : 'Zahlungsübersicht'} geprüft wurde.</span></label>}
                      {email.trim() && email.trim().toLowerCase() !== String(tenantData.email || '').trim().toLowerCase() && <label className="mt-2 flex items-start gap-2 text-xs"><input type="checkbox" checked={saveToTenant} disabled={testEmailReview.status !== 'idle'} onChange={(event) => setSaveToTenant(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" /><span>E-Mail-Adresse nach meiner ausdrücklichen Bestätigung im Mieterprofil speichern.</span></label>}
                    </div>}

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Betreff</label>
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Message Editor */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Nachricht</label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={10}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSend}
                        disabled={sending || (isBatchArrears && activeTab === 'email' && !testEmailReviewReady)}
                        title={isBatchArrears && activeTab === 'email' && !testEmailReviewReady ? 'Bitte zuerst die Überprüfung abschließen' : undefined}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        {sending ? 'Wird vorbereitet...' : (isBatchArrears && activeTab === 'email') ? (testEmailReviewReady ? (testMode ? 'Test-E-Mail jetzt senden' : 'E-Mail jetzt senden') : testEmailReview.status === 'countdown' ? `Überprüfung läuft · ${testEmailReview.remaining} Sek.` : 'Überprüfung starten') : testMode ? 'Test-Dry-Run ausführen' : isBatchArrears ? 'Entwurf abschließen (kein Versand)' : 'E-Mail senden'}
                      </button>
                      <button
                        onClick={() => setShowSaveDialog(true)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Als Vorlage speichern
                      </button>
                    </div>
                  </div>
                )}

                {/* PDF Tab */}
                {activeTab === 'pdf' && (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">PDF-Generierung kommt in Phase 2</p>
                  </div>
                )}

                {/* Legal Tab - Gerichtlicher Mahnbescheid */}
                {activeTab === 'legal' && (
                  <div className="space-y-6">
                    {/* Eligibility Check */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <Scale className="w-6 h-6 text-blue-600 mt-1" />
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Gerichtlicher Mahnbescheid vorbereiten</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Clara360 bereitet alle Daten für den Online-Mahnantrag vor. Sie entscheiden, wann und ob Sie den Antrag stellen.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Eligibility Checklist */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Voraussetzungen</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-green-600 text-xl">✔</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">Forderung fällig ({paymentData.days_overdue} Tage überfällig)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-green-600 text-xl">✔</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">Mahnstufen dokumentiert</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-green-600 text-xl">✔</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">Schuldnerdaten vollständig</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-green-600 text-xl">✔</span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">Kommunikationshistorie vorhanden</span>
                        </div>
                      </div>
                    </div>

                    {/* Forderungsdetails */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Forderungsdetails</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Gläubiger:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{ownerData.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Schuldner:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{tenantData.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Hauptforderung:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{(paymentData.expected_amount - paymentData.actual_amount).toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Zeitraum:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{paymentData.period_month}/{paymentData.period_year}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Überfällig seit:</span>
                          <span className="font-medium text-red-600">{paymentData.days_overdue} Tagen</span>
                        </div>
                      </div>
                    </div>

                    {/* Warning */}
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <p className="font-medium mb-1">Wichtiger Hinweis</p>
                          <p>Clara360 bereitet nur vor - Sie entscheiden über die Antragstellung beim Gericht. Kosten entstehen erst bei Einreichung.</p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => alert('PDF-Vorbereitung folgt in Kürze')}
                        className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium"
                      >
                        <FileText className="w-5 h-5" />
                        Mahnantrag vorbereiten (PDF)
                      </button>
                      <button
                        onClick={() => window.open('https://www.mahngerichte.de/', '_blank')}
                        className="px-4 py-3 border-2 border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2 font-medium"
                      >
                        Zum Justizportal →
                      </button>
                    </div>
                  </div>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                  <div className="space-y-4">
                    {insights?.timeline?.length > 0 ? (
                      insights.timeline.map((event, idx) => (
                        <div key={idx} className="flex gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="text-sm text-gray-500">
                            {new Date(event.date).toLocaleDateString('de-DE')}
                          </div>
                          <div className="flex-1">
                            {event.type === 'payment' ? (
                              <div>
                                <span className={`inline-block px-2 py-1 rounded text-xs ${
                                  event.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {event.status === 'paid' ? 'Bezahlt' : 'Offen'}
                                </span>
                                <span className="ml-2">{event.description} - {event.amount}€</span>
                              </div>
                            ) : (
                              <div>
                                <span className="inline-block px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                                  {event.channel}
                                </span>
                                <span className="ml-2">{event.description}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-8">Keine Historie vorhanden</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Sidebar - Insights */}
        <div className="w-80 border-l border-gray-200 dark:border-gray-700 p-6 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Insights</h3>
          </div>

          {loadingInsights ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : insights ? (
            <div className="space-y-4">
              {/* Risk Score */}
              <div className={`p-4 rounded-lg border-2 ${
                insights.riskScore.color === 'green' ? 'bg-green-50 border-green-200' :
                insights.riskScore.color === 'yellow' ? 'bg-yellow-50 border-yellow-200' :
                'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Risiko-Score</span>
                  <span className={`text-2xl font-bold ${
                    insights.riskScore.color === 'green' ? 'text-green-700' :
                    insights.riskScore.color === 'yellow' ? 'text-yellow-700' :
                    'text-red-700'
                  }`}>
                    {insights.riskScore.level === 'low' ? '🟢' :
                     insights.riskScore.level === 'medium' ? '🟡' : '🔴'}
                  </span>
                </div>
                <p className="text-xs text-gray-600">
                  {insights.riskScore.level === 'low' ? 'Niedriges Risiko' :
                   insights.riskScore.level === 'medium' ? 'Mittleres Risiko' : 'Hohes Risiko'}
                </p>
              </div>

              {/* Payment Behavior */}
              <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="font-medium text-sm mb-2 text-gray-900 dark:text-white">Zahlungsverhalten</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Pünktlich:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{insights.paymentBehavior.onTimeCount} / {insights.paymentBehavior.onTimeCount + insights.paymentBehavior.lateCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Ø Verzug:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{insights.paymentBehavior.avgDelayDays} Tage</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Trend:</span>
                    <span className={`font-medium ${
                      insights.paymentBehavior.trend === 'improving' ? 'text-green-600' :
                      insights.paymentBehavior.trend === 'declining' ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {insights.paymentBehavior.trend === 'improving' ? '↗ Besser' :
                       insights.paymentBehavior.trend === 'declining' ? '↘ Schlechter' : '→ Stabil'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Communication Preference */}
              <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="font-medium text-sm mb-2 text-gray-900 dark:text-white">Kommunikation</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Bevorzugt:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{insights.communicationPreference.preferredChannel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Response-Rate:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{insights.communicationPreference.responseRate}%</span>
                  </div>
                </div>
              </div>

              {/* Recommendation */}
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200">
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-purple-600 mt-0.5" />
                  <h4 className="font-medium text-sm text-gray-900 dark:text-white">Empfehlung</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Kanal:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{insights.recommendation.channel}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Ton:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{insights.recommendation.tone}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Mahnstufe:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{insights.recommendation.dunningLevel}</span>
                  </div>
                  {insights.recommendation.reason && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      {insights.recommendation.reason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">Keine Insights verfügbar</p>
          )}
        </div>
      </div>

      {/* Save Template Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Vorlage speichern</h3>
            <input
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Name der Vorlage"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveTemplate}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
              <button
                onClick={() => setShowSaveDialog(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
